import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text, TextProps } from '@/components/TextComponent';
import { Event } from '@/feature/event-details-sbk/types';
import { useFormatPeriod } from '@/hooks/use-format-period';
import { useEventDetailsCache, useEventStatus } from '@/hooks/use-sbk-event-details-cache';
import { AtLeast } from '@/types/utils';
import { resolveEventTimeInfo } from '@/utils/event-time';
import { useShallow } from 'zustand/react/shallow';

/** Structural so any event carrying these fields qualifies (PAM `Event`, bets-sbk `BetEvent`). */
type EventTimeEvent = AtLeast<
    Event,
    'id' | 'status' | 'start_time' | 'event_details' | 'sport' | 'home_team' | 'away_team'
>;

type EventTimeProps = {
    event: EventTimeEvent;
} & TextProps;

/**
 * Returns the current time of the event. The display time varies based on sport and current status of the game
 * FINISHED - "Final"
 * LIVE
 *   - Basketball: "1st, 8:23"
 *   - American Football: "1st, 12:34"
 *   - Ice Hockey: "1st, 19:00"
 *   - Baseball: "Top 7"
 * NOTSTARTED - Date
 */
export const useEventTime = (event: EventTimeEvent) => {
    const { t } = useTranslation(['common', 'event_time']);
    const eventStatus = useEventStatus(event.id) ?? event.status;

    const { periodName, gameClock, sportSpecific } = useEventDetailsCache(
        useShallow(state => {
            return {
                periodName: state.eventDetails[event.id]?.period_name ?? event.event_details?.period_name,
                gameClock: state.eventDetails[event.id]?.game_clock ?? event.event_details?.game_clock,
                sportSpecific: state.eventDetails[event.id]?.sport_specific ?? event.event_details?.sport_specific,
            };
        })
    );

    const eventTime = useFormatPeriod({ event, periodName, gameClock, sportSpecific });

    if (eventStatus === 'FINISHED') {
        // TODO to handle OT cases
        return t('final');
    }
    return resolveEventTimeInfo({
        periodTime: eventTime,
        isLive: eventStatus === 'LIVE',
        startTime: event.start_time,
        liveLabel: t('event_time:live'),
    });
};

export const EventTime = ({ event, ...textProps }: EventTimeProps) => {
    const time = useEventTime(event);
    return (
        <Text testID="eventPageEventTime" {...textProps}>
            {time}
        </Text>
    );
};
